import React, { createContext, useContext, useState, useRef, useMemo, useCallback } from 'react';

const PlayerStateContext = createContext(null);
const PlayerActionsContext = createContext(null);

export function PlayerProvider({ children }) {
  const [currentSong, setCurrentSong] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [showVideo, setShowVideo] = useState(false);
  const [progress, setProgress] = useState({ current: 0, total: 0 });
  const [volume, setVolume] = useState(80);
  const [seekTo, setSeekTo] = useState(null);
  const [playlist, setPlaylist] = useState([]);
  const [playedIds, setPlayedIds] = useState(() => new Set());

  // Refs let actions read the latest state without depending on it, so the
  // actions object stays referentially stable and its context never notifies.
  const currentSongRef = useRef(currentSong);
  currentSongRef.current = currentSong;
  const playlistRef = useRef(playlist);
  playlistRef.current = playlist;

  const playSong = useCallback((song, currentPlaylist = []) => {
    const songId = song?.id || song?._id;
    setPlayedIds(prev => {
      if (prev.has(songId)) return prev;
      const next = new Set(prev);
      next.add(songId);
      return next;
    });

    if (currentSongRef.current?.id === songId) {
      setIsPlaying(true);
      return;
    }
    if (currentPlaylist.length > 0) {
      setPlaylist(currentPlaylist);
    }
    setCurrentSong(song);
    setIsPlaying(true);
    setProgress({ current: 0, total: 0 });
    setSeekTo(null);
  }, []);

  const playNext = useCallback(() => {
    const pl = playlistRef.current;
    if (pl.length === 0) return;
    const cur = currentSongRef.current;
    const currentIndex = pl.findIndex(s => s.id === cur?.id || s._id === cur?._id);
    if (currentIndex !== -1 && currentIndex < pl.length - 1) {
      playSong(pl[currentIndex + 1]);
    }
  }, [playSong]);

  const playPrevious = useCallback(() => {
    const pl = playlistRef.current;
    if (pl.length === 0) return;
    const cur = currentSongRef.current;
    const currentIndex = pl.findIndex(s => s.id === cur?.id || s._id === cur?._id);
    if (currentIndex > 0) {
      playSong(pl[currentIndex - 1]);
    }
  }, [playSong]);

  const seek = useCallback((time) => setSeekTo(time), []);
  const togglePlay = useCallback(() => setIsPlaying(prev => !prev), []);

  // Stable — created once, so consumers that only need actions never re-render.
  const actions = useMemo(() => ({
    playSong, togglePlay, playNext, playPrevious, seek,
    setCurrentSong, setIsPlaying, setShowVideo,
    setProgress, setVolume, setSeekTo, setPlaylist,
  }), [playSong, togglePlay, playNext, playPrevious, seek]);

  const state = useMemo(() => ({
    currentSong, isPlaying, showVideo, progress, volume, seekTo, playlist, playedIds,
  }), [currentSong, isPlaying, showVideo, progress, volume, seekTo, playlist, playedIds]);

  return (
    <PlayerActionsContext.Provider value={actions}>
      <PlayerStateContext.Provider value={state}>
        {children}
      </PlayerStateContext.Provider>
    </PlayerActionsContext.Provider>
  );
}

export const usePlayerState = () => {
  const ctx = useContext(PlayerStateContext);
  if (!ctx) throw new Error('usePlayerState must be used within a PlayerProvider');
  return ctx;
};

export const usePlayerActions = () => {
  const ctx = useContext(PlayerActionsContext);
  if (!ctx) throw new Error('usePlayerActions must be used within a PlayerProvider');
  return ctx;
};

// Back-compat: existing components that read a mix of state and actions.
// Prefer usePlayerActions() when you only need actions — it never re-renders.
export const usePlayer = () => {
  const state = usePlayerState();
  const actions = usePlayerActions();
  return { ...state, ...actions };
};
